import { useEffect, useRef, useState } from "react";

import { Button, Link, LogSentryException, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type { ChangeEvent, FormEvent } from "react";

import { useSimulateTabFocusInApp } from "../../hooks/useSimulateTabFocusInApp";
import useTranslation from "../../hooks/useTranslation";
import { AuthError } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";

import style from "./PasswordReset.module.css";

interface PasswordResetProps {
  onSubmit: (email: string, captcha: string) => void;
  onReset: () => void;
  onBackClick: () => void;

  inProgress: boolean;
  submitSuccess: boolean;
  submitError?: AuthError | null;
}

const PasswordReset = (props: PasswordResetProps) => {
  useSimulateTabFocusInApp();

  const [email, setEmail] = useState("");
  const [displayedErrorMessage, setDisplayedErrorMessage] = useState("");
  const { t, m } = useTranslation();

  const errorMessage = {
    InvalidEmail: t(m.ErrorMessage.InvalidEmail),
    CaptchaError: t(m.ErrorMessage.CaptchaError),
    Unknown: t(m.ErrorMessage.Unknown),
    PasswordUnchanged: t(m.ErrorMessage.PasswordUnchanged),
  };
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);

  useEffect(() => {
    if (props.submitError === AuthError.InvalidEmail) {
      setDisplayedErrorMessage(errorMessage.InvalidEmail);
    } else if (props.submitError === AuthError.MissingCaptcha) {
      setDisplayedErrorMessage(errorMessage.CaptchaError);
    } else if (props.submitError === AuthError.PasswordUnchanged) {
      setDisplayedErrorMessage(errorMessage.PasswordUnchanged);
    } else if (props.submitError === AuthError.Unknown) {
      setDisplayedErrorMessage(errorMessage.Unknown);
    }
  }, [
    props.submitError,
    errorMessage.InvalidEmail,
    errorMessage.Unknown,
    errorMessage.CaptchaError,
    errorMessage.PasswordUnchanged,
  ]);

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleSubmitForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      return setDisplayedErrorMessage(errorMessage.InvalidEmail);
    }

    try {
      const recaptchaValue = await recaptchaRef.current?.execute();
      if (recaptchaValue) {
        props.onSubmit(email, recaptchaValue);
      }
    } catch (error) {
      setDisplayedErrorMessage(errorMessage.CaptchaError);
      LogSentryException({ messagePrefix: "Captcha error", error });
    }
  };

  const handleReset = () => {
    setDisplayedErrorMessage("");
    props.onReset();
  };

  const renderForm = () => {
    const { inProgress } = props;
    const hasError =
      displayedErrorMessage === errorMessage.InvalidEmail ||
      displayedErrorMessage === errorMessage.CaptchaError ||
      displayedErrorMessage === errorMessage.PasswordUnchanged;

    return (
      <>
        <Heading type="h1">{t(m.ResetPassword.Title)}</Heading>
        <div>
          <p>{t(m.ResetPassword.Instructions)}</p>
        </div>
        <form onSubmit={handleSubmitForm} noValidate>
          <TextField
            tabIndex={0}
            className={classNames(style.inputField, "email-input")}
            type="email"
            placeholder={t(m.General.Email)}
            value={email}
            onChange={handleEmailChange}
            error={hasError}
            errorMessage={displayedErrorMessage}
            disabled={inProgress}
            autoFocus
          />

          <Button
            tabIndex={0}
            type="submit"
            className={classNames(style.button, style.modalFormSubmit)}
            disabled={inProgress}
          >
            {t(m.ResetPassword.SendResetLink)}
          </Button>
          <ReCAPTCHA ref={recaptchaRef} />
        </form>

        <div className={style.footer}>
          <Button
            tabIndex={0}
            className={classNames(style.button, "back-button")}
            variant="grayscale"
            onClick={props.onBackClick}
          >
            {t(m.ResetPassword.BackToLogin)}
          </Button>
        </div>
      </>
    );
  };

  const renderSuccess = () => {
    return (
      <>
        <Heading type="h1">{t(m.ResetPassword.EmailSent)}</Heading>
        <div>
          <p>
            <span>{t(m.ResetPassword.SuccessMessage)}</span> <strong>{email}</strong>
          </p>
          <p>{t(m.ResetPassword.CantFindEmail)}</p>
        </div>
        <div className={classNames(style.footer, style.footerSuccess)}>
          <Link onClick={props.onBackClick}>{t(m.ResetPassword.BackToLogin)}</Link>
          <Link onClick={handleReset}>{t(m.ResetPassword.SendAgain)}</Link>
        </div>
      </>
    );
  };

  return (
    <FormWrapper className={style.passwordReset}>
      <div className={style.modalContainer}>
        {props.submitSuccess ? renderSuccess() : renderForm()}
      </div>
    </FormWrapper>
  );
};

export default PasswordReset;
